import React, { useEffect, useState, useMemo, useCallback } from "react";
import { CardHeader, CardTitle, CardDescription, Card, CardContent } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "./ui/pagination";
import { ToggleGroup, ToggleGroupItem } from "./ui/toggle-group";
import { ChartContainer, ChartTooltipContent, chartConfig } from "./ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from "recharts";
import fetchWithAuth from "../fetchWithAuth";

// Вернуть YYYY-MM-DD
function toYMD(date) {
  const d = new Date(date);
  return d.toISOString().slice(0, 10);
}

// Форматирование даты: dd.mm.yyyy
function formatDate(date) {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  const pad = n => n.toString().padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

// Парсинг dd.mm.yyyy → Date
function parseFormatted(str) {
  const [day, month, year] = str.split(".");
  return new Date(`${year}-${month}-${day}`);
}

export default function RepeatStatsTable({ projectId }) {
  const [stats, setStats] = useState([]);
  const [allStats, setAllStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [promotedPages, setPromotedPages] = useState([]);
  const [blacklistPages, setBlacklistPages] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [period, setPeriod] = useState("week");
  const itemsPerPage = 30;

  // Загрузка страниц
  useEffect(() => {
    if (!projectId) return;
    fetchWithAuth(`/api/project/${projectId}/promoted`)
      .then(res => res.json())
      .then(data => setPromotedPages(data || []))
      .catch(() => setPromotedPages([]));
    fetchWithAuth(`/api/project/${projectId}/blacklist`)
      .then(res => res.json())
      .then(data => setBlacklistPages(data || []))
      .catch(() => setBlacklistPages([]));
  }, [projectId]);

  const fetchData = useCallback((currentPage) => {
    if (!projectId) return;
    setLoading(true);
    fetchWithAuth(`/api/project/${projectId}/repeat-stats?page=${currentPage}&pageSize=${itemsPerPage}`)
      .then(res => res.json())
      .then(response => {
        if (response && response.data && Array.isArray(response.data)) {
          const sorted = [...response.data].sort((a, b) => new Date(b.date) - new Date(a.date));
          setStats(sorted);
          setTotalPages(Math.ceil((response.total || 0) / itemsPerPage));
        } else {
          setStats([]);
          setTotalPages(1);
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Ошибка загрузки статистики");
        setLoading(false);
      });

    fetchWithAuth(`/api/project/${projectId}/repeat-stats?all=true`)
      .then(res => res.json())
      .then(response => {
        if (response && response.data && Array.isArray(response.data)) {
          const sorted = [...response.data].sort((a, b) => new Date(a.date) - new Date(b.date));
          setAllStats(sorted);
        } else {
          setAllStats([]);
        }
      })
      .catch(() => console.error("Ошибка загрузки данных для графика"));
  }, [projectId, itemsPerPage]);

  useEffect(() => {
    fetchData(page);
  }, [projectId, page, fetchData]);

  // startDate для текущего периода
  const periodStartDate = useMemo(() => {
    if (period === "all") return null;
    const now = new Date();
    const start = new Date(now);
    if (period === "week") start.setDate(now.getDate() - 7);
    else if (period === "month") start.setMonth(now.getMonth() - 1);
    else if (period === "quarter") start.setMonth(now.getMonth() - 3);
    return start;
  }, [period]);

  // Фильтрация данных для графика
  const filteredChartData = useMemo(() => {
    if (!allStats.length) return [];
    if (period === "all") return allStats;
    return allStats.filter(stat => {
      const statDate = new Date(stat.date);
      return !Number.isNaN(statDate.getTime()) && statDate >= periodStartDate;
    });
  }, [allStats, period, periodStartDate]);

  // Группировка дат добавления продвигаемых (только в рамках периода)
  const promotedRefLines = useMemo(() => {
    const map = new Map();
    for (const p of promotedPages) {
      if (!p.createdAt) continue;
      const d = new Date(p.createdAt);
      if (periodStartDate && d < periodStartDate) continue;
      const dateStr = formatDate(d);
      map.set(dateStr, (map.get(dateStr) || 0) + 1);
    }
    return Array.from(map.entries()).map(([date, count]) => ({ date, count }));
  }, [promotedPages, periodStartDate]);

  // Группировка дат добавления вытесняемых (только в рамках периода)
  const blacklistRefLines = useMemo(() => {
    const map = new Map();
    for (const p of blacklistPages) {
      if (!p.createdAt) continue;
      const d = new Date(p.createdAt);
      if (periodStartDate && d < periodStartDate) continue;
      const dateStr = formatDate(d);
      map.set(dateStr, (map.get(dateStr) || 0) + 1);
    }
    return Array.from(map.entries()).map(([date, count]) => ({ date, count }));
  }, [blacklistPages, periodStartDate]);

  // Данные графика: объединяем стат-данные + даты добавления страниц с null-значениями
  const chartData = useMemo(() => {
    const dateMap = new Map();

    // Статистические данные
    for (const stat of filteredChartData) {
      const dateStr = formatDate(new Date(stat.date));
      dateMap.set(dateStr, {
        date: dateStr,
        "Продвигаемые топ-10": stat.sumPosRepTop10,
        "Продвигаемые топ-20": stat.sumPosRepTop20,
        "Вытесняемые топ-10": stat.sumNegRepTop10,
        "Вытесняемые топ-20": stat.sumNegRepTop20,
      });
    }

    // Даты добавления страниц — добавляем с null если нет в статистике
    const allPageDates = [
      ...promotedRefLines.map(r => r.date),
      ...blacklistRefLines.map(r => r.date),
    ];
    for (const dateStr of allPageDates) {
      if (!dateMap.has(dateStr)) {
        dateMap.set(dateStr, {
          date: dateStr,
          "Продвигаемые топ-10": null,
          "Продвигаемые топ-20": null,
          "Вытесняемые топ-10": null,
          "Вытесняемые топ-20": null,
        });
      }
    }

    // Сортируем по дате
    return Array.from(dateMap.values()).sort((a, b) => parseFormatted(a.date) - parseFormatted(b.date));
  }, [filteredChartData, promotedRefLines, blacklistRefLines]);

  return (
    <div className="space-y-6">
      {/* График */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle>Динамика страниц</CardTitle>
            <CardDescription>
              {period === "week" && "За последнюю неделю"}
              {period === "month" && "За последний месяц"}
              {period === "quarter" && "За последние 3 месяца"}
              {period === "all" && "За все время"}
            </CardDescription>
          </div>
          <ToggleGroup
            type="single"
            value={period}
            onValueChange={(value) => value && setPeriod(value)}
            className="border rounded-md"
          >
            <ToggleGroupItem value="week">Неделя</ToggleGroupItem>
            <ToggleGroupItem value="month">Месяц</ToggleGroupItem>
            <ToggleGroupItem value="quarter">3 месяца</ToggleGroupItem>
            <ToggleGroupItem value="all">За все время</ToggleGroupItem>
          </ToggleGroup>
        </CardHeader>
        <CardContent>
          {loading && (
            <div className="flex justify-center items-center h-[350px]">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}

          {!loading && chartData.length === 0 && (
            <div className="flex justify-center items-center h-[350px] text-gray-500">
              Нет данных для отображения графика
            </div>
          )}

          {!loading && chartData.length > 0 && (
            <ChartContainer>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="date"
                    stroke="#888888"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="#888888"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `${value}`}
                  />
                  <Tooltip
                    content={<ChartTooltipContent />}
                    cursor={{ stroke: "#f3f4f6", strokeWidth: 1 }}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: 10 }}
                    verticalAlign="bottom"
                    height={36}
                  />
                  <Line
                    type="monotone"
                    dataKey="Продвигаемые топ-10"
                    stroke={chartConfig.colors.posTop10}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="Продвигаемые топ-20"
                    stroke={chartConfig.colors.posTop20}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="Вытесняемые топ-10"
                    stroke={chartConfig.colors.negTop10}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="Вытесняемые топ-20"
                    stroke={chartConfig.colors.negTop20}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />

                  {/* Вертикальные черты — продвигаемые (тёмно-зелёные, +5px вправо) */}
                  {promotedRefLines.map(({ date, count }) => (
                    <ReferenceLine
                      key={`promoted-${date}`}
                      x={date}
                      stroke="none"
                      label={(props) => {
                        const { viewBox } = props;
                        if (!viewBox) return null;
                        const { x, y, height } = viewBox;
                        const xPos = x + 5;
                        return (
                          <g>
                            <line x1={xPos} y1={y} x2={xPos} y2={y + height} stroke="#2E7D32" strokeWidth={2} />
                            <text x={xPos + 3} y={y + 13} fill="#2E7D32" fontSize={11} fontWeight="600" textAnchor="start" dominantBaseline="auto">{`+${count}`}</text>
                          </g>
                        );
                      }}
                    />
                  ))}

                  {/* Вертикальные черты — вытесняемые (тёмно-красные, -5px влево) */}
                  {blacklistRefLines.map(({ date, count }) => (
                    <ReferenceLine
                      key={`blacklist-${date}`}
                      x={date}
                      stroke="none"
                      label={(props) => {
                        const { viewBox } = props;
                        if (!viewBox) return null;
                        const { x, y, height } = viewBox;
                        const xPos = x - 5;
                        return (
                          <g>
                            <line x1={xPos} y1={y} x2={xPos} y2={y + height} stroke="#C62828" strokeWidth={2} strokeDasharray="4 2" />
                            <text x={xPos - 3} y={y + 13} fill="#C62828" fontSize={11} fontWeight="600" textAnchor="end" dominantBaseline="auto">{`+${count}`}</text>
                          </g>
                        );
                      }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          )}
        </CardContent>
      </Card>

      {/* Таблица */}
      <CardHeader className="px-0 pt-0">
        <CardTitle>Повторы продвигаемых и вытесняемых страниц</CardTitle>
      </CardHeader>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[75px]">Дата</TableHead>
                <TableHead className="w-[150px]">Продвигаемые топ-10</TableHead>
                <TableHead className="w-[150px]">Продвигаемые топ-20</TableHead>
                <TableHead className="w-[150px]">Вытесняемые топ-10</TableHead>
                <TableHead className="w-[150px]">Вытесняемые топ-20</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats.map((stat, idx) => {
                function getAddedCounts(dateStr) {
                  const date = new Date(dateStr);
                  const prev = new Date(date);
                  prev.setDate(date.getDate() - 1);
                  const prevYMD = toYMD(prev);
                  const nPromoted = promotedPages.filter(
                    p => p.createdAt && toYMD(p.createdAt) === prevYMD
                  ).length;
                  const nBlacklist = blacklistPages.filter(
                    p => p.createdAt && toYMD(p.createdAt) === prevYMD
                  ).length;
                  return { nPromoted, nBlacklist };
                }

                const { nPromoted, nBlacklist } = getAddedCounts(stat.date);

                return (
                  <TableRow key={stat.id || idx}>
                    <TableCell className="w-[125px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{formatDate(new Date(stat.date))}</span>
                        {nPromoted !== 0 && (
                          <Badge className="bg-green-500 text-white border-transparent text-xs px-1.5 py-0.5 whitespace-nowrap">
                            +{nPromoted}
                          </Badge>
                        )}
                        {nBlacklist !== 0 && (
                          <Badge className="bg-red-500 text-white border-transparent text-xs px-1.5 py-0.5 whitespace-nowrap">
                            +{nBlacklist}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="w-[150px]">{stat.sumPosRepTop10}</TableCell>
                    <TableCell className="w-[150px]">{stat.sumPosRepTop20}</TableCell>
                    <TableCell className="w-[150px]">{stat.sumNegRepTop10}</TableCell>
                    <TableCell className="w-[150px]">{stat.sumNegRepTop20}</TableCell>
                  </TableRow>
                );
              })}
              {stats.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-4 text-gray-500">
                    Нет данных о повторах
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Пагинация */}
      {!loading && stats.length > 0 && (
        <div className="mt-4">
          <Pagination>
            <PaginationContent>
              {page > 1 && (
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => { e.preventDefault(); setPage(page - 1); }}
                  />
                </PaginationItem>
              )}

              {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
                let pageNumber;

                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (page <= 3) {
                  pageNumber = i + 1;
                  if (i === 4) return <PaginationItem key={i}><PaginationEllipsis /></PaginationItem>;
                } else if (page >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                  if (i === 0) return <PaginationItem key={i}><PaginationEllipsis /></PaginationItem>;
                } else {
                  if (i === 0) return (
                    <PaginationItem key={i}>
                      <PaginationLink href="#" onClick={(e) => { e.preventDefault(); setPage(1); }}>1</PaginationLink>
                    </PaginationItem>
                  );
                  if (i === 1) return <PaginationItem key={i}><PaginationEllipsis /></PaginationItem>;
                  if (i === 3) return <PaginationItem key={i}><PaginationEllipsis /></PaginationItem>;
                  if (i === 4) return (
                    <PaginationItem key={i}>
                      <PaginationLink href="#" onClick={(e) => { e.preventDefault(); setPage(totalPages); }}>{totalPages}</PaginationLink>
                    </PaginationItem>
                  );
                  pageNumber = page + i - 2;
                }

                return (
                  <PaginationItem key={i}>
                    <PaginationLink
                      href="#"
                      isActive={pageNumber === page}
                      onClick={(e) => { e.preventDefault(); setPage(pageNumber); }}
                    >
                      {pageNumber}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}

              {page < totalPages && (
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => { e.preventDefault(); setPage(page + 1); }}
                  />
                </PaginationItem>
              )}
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
